import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { registerUserWithEmails, acceptEditorInvitation } from '../../graphqlService';
import TermsAndConditions from '../Legal/TermsAndConditions';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import { isPasswordValid } from '../../utils/passwordPolicy';
import { debugLog } from '../../utils/debugLog';
import { getApolloErrorMessage } from '../../utils/apolloError';
import AuthLayout from './AuthLayout';
import AuthStatus from './AuthStatus';
import PasswordField from './PasswordField';
import { AuthErrorField, describeAuthError, getAuthErrorCode, isNetworkFailure, NETWORK_ERROR_MESSAGE } from './authErrors';
import { FIELD_ERRORS, FieldErrors, focusFirstInvalid, SUPPORT_HREF } from './authForm';
import { checkInvitationLink, INVITATION_PROBLEM_COPY } from './authLinks';

type RegistrationField = 'name' | 'password' | 'confirmPassword' | 'terms';
const FIELD_ORDER: readonly RegistrationField[] = ['name', 'password', 'confirmPassword', 'terms'];

const REGISTRATION_FAILED_MESSAGE =
  "We couldn't finish creating your account. Please try again, or contact support if it keeps happening.";
const TERMS_REQUIRED = 'Agree to the Editor Terms and Conditions to continue.';

/** Firebase and transport text that should never reach the page as-is. */
const TECHNICAL_ERROR_PATTERN =
  /firebase|auth\/|failed to fetch|networkerror|network error|status code|unexpected error/i;

interface RegistrationFailure {
  message: string;
  /** The email already has an account: offer sign-in. */
  offerSignIn: boolean;
  /** The problem belongs to one field (e.g. a password Firebase refused). */
  field?: AuthErrorField;
}

/**
 * Plain text for a failed registration. Firebase used to come through raw
 * ("Firebase: Error (auth/email-already-in-use).") when an editor opened
 * their invitation link again after registering.
 */
const describeRegistrationError = (error: unknown): RegistrationFailure => {
  if (getAuthErrorCode(error)) {
    const copy = describeAuthError(error, 'editor-registration');
    return { message: copy.message, offerSignIn: copy.action === 'sign-in', field: copy.field };
  }
  if (isNetworkFailure(error)) {
    return { message: NETWORK_ERROR_MESSAGE, offerSignIn: false };
  }
  // GraphQL errors the API raises on purpose are written for customers;
  // anything that reads like Firebase or transport internals is not.
  const message = error ? getApolloErrorMessage(error).trim() : '';
  if (!message || TECHNICAL_ERROR_PATTERN.test(message)) {
    return { message: REGISTRATION_FAILED_MESSAGE, offerSignIn: false };
  }
  return { message, offerSignIn: false };
};

const EditorRegistration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Cleared on unmount so a navigate() can't fire after the component is gone
  // (and so StrictMode's double-mount doesn't schedule two redirects).
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  // The same checks the invitation page makes: readable, not expired, sent
  // to this address, and an invitation.
  const invitationToken = searchParams.get('token');
  const invitedEmail = searchParams.get('email');
  const linkProblem = useMemo(
    () => checkInvitationLink(invitationToken, invitedEmail),
    [invitationToken, invitedEmail]
  );
  const email = invitedEmail || '';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<RegistrationField>>({});
  const [failure, setFailure] = useState<RegistrationFailure | null>(null);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const refs = {
    name: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    confirmPassword: useRef<HTMLInputElement>(null),
    terms: useRef<HTMLInputElement>(null),
  };

  const clearFieldError = (...fields: RegistrationField[]) => {
    if (fields.some((field) => fieldErrors[field])) {
      setFieldErrors((current) => {
        const next = { ...current };
        fields.forEach((field) => delete next[field]);
        return next;
      });
    }
  };

  const validate = (): FieldErrors<RegistrationField> => {
    const errors: FieldErrors<RegistrationField> = {};
    if (!name.trim()) errors.name = FIELD_ERRORS.nameRequired;
    // Must match the shared policy (and the backend). Accepting 6-character
    // passwords here let invited editors set a password that Signup and
    // Reset Password would both reject, locking them out of recovery.
    if (!password) errors.password = FIELD_ERRORS.newPasswordRequired;
    else if (!isPasswordValid(password)) errors.password = FIELD_ERRORS.newPasswordWeak;
    if (!confirmPassword) errors.confirmPassword = FIELD_ERRORS.confirmRequired;
    else if (password !== confirmPassword) errors.confirmPassword = FIELD_ERRORS.confirmMismatch;
    if (!agreeToTerms) errors.terms = TERMS_REQUIRED;
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // The account already exists once `registered` is set; a second submit
    // during the redirect delay would only fail with "email already in use".
    if (loading || registered || linkProblem) return;

    setFailure(null);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(FIELD_ORDER, errors, refs);
      return;
    }

    setLoading(true);
    const displayName = name.trim();
    // The same invitation token the page checked above; undefined (never null
    // or '') when the link somehow lacks one.
    const token = invitationToken || undefined;

    try {
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Register user in database with editor role
      const registrationResult = await registerUserWithEmails(
        userCredential.user.uid,
        email,
        displayName,
        'editor', // Editor role only
        false, // No welcome email needed
        // The backend pre-verifies an invited editor's email only when this is
        // a valid invitation for that address; otherwise it sends the normal
        // verification email.
        token
      );

      if (!registrationResult.success) {
        // The backend's error here is raw server text; log it, show plain copy.
        console.error('Editor registration was rejected by the API:', registrationResult.error);
        throw new Error(REGISTRATION_FAILED_MESSAGE);
      }

      // Update display name in Firebase
      if (userCredential.user) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Accept the editor invitation to link Firebase UID and update status
      try {
        if (token) {
          const acceptResult = await acceptEditorInvitation(token, email);
          if (acceptResult.success) {
            debugLog('Editor invitation accepted successfully');
          } else {
            console.warn('Registration succeeded but invitation acceptance failed:', acceptResult.message);
            // Still allow user to continue - they can try to accept later via dashboard
          }
        }
      } catch (acceptError) {
        console.error('Failed to accept editor invitation after registration:', acceptError);
        // Don't block user from proceeding - registration was successful
      }

      // acceptEditorInvitation verifies the account server-side via the Admin
      // SDK, but the client's cached Firebase user snapshot still says
      // emailVerified: false — and AuthContext treats unverified users as
      // signed out, so navigating straight to /edit-projects bounced the new
      // editor to /login. Refresh the snapshot and the ID token first.
      try {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
        }
      } catch (refreshError) {
        console.warn('Could not refresh verification state after registration:', refreshError);
      }

      // Say so during the redirect delay; the form used to sit there unchanged.
      setRegistered(true);
      redirectTimerRef.current = setTimeout(() => {
        navigate('/edit-projects');
      }, 2000);
    } catch (error: unknown) {
      console.error('Registration error:', error);
      const described = describeRegistrationError(error);
      if (described.field === 'password') {
        const fieldError = { password: described.message };
        setFieldErrors(fieldError);
        focusFirstInvalid(FIELD_ORDER, fieldError, refs);
      } else {
        setFailure(described);
      }
    } finally {
      setLoading(false);
    }
  };

  if (linkProblem) {
    const copy = INVITATION_PROBLEM_COPY[linkProblem];
    return (
      <AuthLayout>
        <AuthStatus
          tone="error"
          icon={<LinkOffOutlinedIcon />}
          title={copy.title}
          description={copy.description}
          primaryAction={{ label: 'Go to sign in', to: '/login' }}
          secondaryAction={{ label: 'Contact support', href: SUPPORT_HREF }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set up your editor account"
      subtitle="Your invitation confirms your email address, so you can start right away."
    >
      {registered && (
        <Alert severity="success" sx={{ mb: 3 }} role="status">
          Account created. Taking you to your projects…
        </Alert>
      )}

      {failure && (
        <Alert
          severity={failure.offerSignIn ? 'warning' : 'error'}
          sx={{ mb: 3 }}
          action={
            failure.offerSignIn ? (
              <Button component={RouterLink} to="/login" color="inherit" size="small">
                Sign in
              </Button>
            ) : undefined
          }
        >
          {failure.message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'grid', gap: 2.5 }}>
        <TextField
          id="email"
          name="email"
          type="email"
          label="Email address"
          autoComplete="email"
          fullWidth
          value={email}
          InputProps={{ readOnly: true }}
          helperText="The address your invitation was sent to"
        />

        <TextField
          inputRef={refs.name}
          id="fullName"
          name="fullName"
          label="Your name"
          autoComplete="name"
          autoFocus
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            clearFieldError('name');
          }}
          error={Boolean(fieldErrors.name)}
          helperText={fieldErrors.name || 'Shown to creators you work with'}
        />

        <PasswordField
          ref={refs.password}
          id="password"
          name="password"
          label="Password"
          autoComplete="new-password"
          showRequirements
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            clearFieldError('password', 'confirmPassword');
          }}
          error={Boolean(fieldErrors.password)}
          helperText={fieldErrors.password}
        />

        <PasswordField
          ref={refs.confirmPassword}
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm password"
          autoComplete="new-password"
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            clearFieldError('confirmPassword');
          }}
          error={Boolean(fieldErrors.confirmPassword)}
          helperText={fieldErrors.confirmPassword}
        />

        <FormControl error={Boolean(fieldErrors.terms)}>
          <FormControlLabel
            control={
              <Checkbox
                inputRef={refs.terms}
                checked={agreeToTerms}
                onChange={(event) => {
                  setAgreeToTerms(event.target.checked);
                  clearFieldError('terms');
                }}
                inputProps={{
                  'aria-describedby': fieldErrors.terms ? 'terms-error' : undefined,
                  'aria-invalid': fieldErrors.terms ? true : undefined,
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I agree to the{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(event: React.MouseEvent) => {
                    // Inside the label: open the terms without ticking the box.
                    event.preventDefault();
                    setShowTermsDialog(true);
                  }}
                  sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                >
                  Editor Terms and Conditions
                </Link>
              </Typography>
            }
          />
          {fieldErrors.terms && (
            <FormHelperText id="terms-error" sx={{ mt: 0 }}>
              {fieldErrors.terms}
            </FormHelperText>
          )}
        </FormControl>

        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading || registered}>
          {loading ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
              Creating account…
            </>
          ) : registered ? (
            'Account created'
          ) : (
            'Create editor account'
          )}
        </Button>
      </Box>

      {/* Terms Dialog */}
      <TermsAndConditions
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        onAccept={() => {
          setAgreeToTerms(true);
          clearFieldError('terms');
          setShowTermsDialog(false);
        }}
        showAcceptButton={true}
        termsFile="EDITOR_TERMS_AND_CONDITIONS.md"
      />
    </AuthLayout>
  );
};

export default EditorRegistration;
